import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ButtonSp from '../../components/ButtonSp';
import InputTextSp from '../../components/InputTextSp';
import LabelSp from '../../components/LabelSp';
import MessageInputText from '../../components/MessageInputText';

import AuthService from '../../services/AuthService';
import LicencaService from '../../services/LicencaService';

import { errorHandle } from '../../util/functions';
import { NOMES_SITUACAO, SITUACAO, formataData, formataDataHora, formataVersao } from './util';
import LicencaCrudEvento from './crudEvento';

// Detalhe de uma chave/instalacao: quem gerou e quando, o servidor que ativou, a situacao
// comercial calculada pela central e os sinais de clone na janela. Acoes: revogar a
// instalacao ativa (ou cancelar a chave nao usada) e desbloquear o limite de clone.
function LicencaCrud(props) {
  LicencaCrud.propTypes = {
    idSelected: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  const { idSelected, onClose } = props;

  const [licenca, setLicenca] = useState({});
  const [dialogo, setDialogo] = useState(''); // '' | 'revogar' | 'desbloquear'
  const [recarregarEventos, setRecarregarEventos] = useState(0);

  const loadRecord = useCallback(async _id => {
    try {
      setLicenca(await LicencaService.findById(_id));
    } catch (err) {
      errorHandle(err);
    }
  }, []);

  useEffect(() => {
    loadRecord(idSelected);
  }, [loadRecord, idSelected]);

  const podeRevogar = AuthService.checkRoles(['LICENCA_ALTERAR', 'LICENCA_GERAR_CHAVE']);
  const podeDesbloquear = AuthService.checkRoles('LICENCA_ALTERAR');
  const ativa = licenca.situacao === SITUACAO.ATIVA;
  const pendente = licenca.situacao === SITUACAO.AGUARDANDO_ATIVACAO;
  const cliente = licenca.cliente || {};
  const usuario = licenca.usuario || {};

  async function confirmaDialogo(motivo) {
    const acao = dialogo;
    setDialogo('');
    try {
      if (acao === 'revogar') {
        await LicencaService.revogar(licenca.id, motivo);
        toast.success(
          ativa
            ? 'Instalação revogada. O servidor para no próximo ciclo do agente.'
            : 'Chave cancelada.'
        );
      } else {
        await LicencaService.desbloquear(licenca.idCliente, motivo);
        toast.success('Contagem de clone zerada para o contrato.');
      }
      await loadRecord(licenca.id);
      setRecarregarEventos(recarregarEventos + 1);
    } catch (err) {
      errorHandle(err);
    }
  }

  function campo(label, valor, classe = 'p-col-6 p-sm-4 p-lg-2 p-fluid') {
    return (
      <div className={classe}>
        <LabelSp>{label}</LabelSp>
        <InputTextSp
          value={valor === undefined || valor === null ? '' : String(valor)}
          disabled
        />
      </div>
    );
  }

  const larga = 'p-col-6 p-sm-4 p-lg-3 p-fluid';

  return (
    <>
      {campo('Contrato', cliente.numeroContrato)}
      {campo('Cliente', cliente.nome, 'p-col-12 p-sm-8 p-lg-6 p-fluid')}
      {campo('Situação', NOMES_SITUACAO[licenca.situacao])}
      {campo('Cliente inativo', cliente.inativo ? 'Sim' : 'Não')}

      {campo('Chave gerada em', formataDataHora(licenca.dataHoraEmissaoChave), larga)}
      {campo('Chave gerada por', usuario.login, larga)}
      {campo('Chave vale até', formataDataHora(licenca.dataHoraVencimentoChave), larga)}
      {campo('Sinais de clone (30 dias)', `${licenca.clonesNaJanela || 0} de 3`, larga)}

      {campo('Servidor', licenca.nomeMaquina, larga)}
      {campo('Versão do agente', formataVersao(licenca.versaoAgente))}
      {campo('Id da base', licenca.idInstalacao, 'p-col-12 p-sm-6 p-lg-4 p-fluid')}
      {campo('Ativada em', formataDataHora(licenca.dataHoraAtivacao), larga)}

      {campo('Última renovação', formataDataHora(licenca.dataHoraUltimaRenovacao), larga)}
      {campo('Vencimento', formataData(licenca.vencimento))}
      {campo('Bloqueia em', formataData(licenca.bloqueiaEm))}

      {licenca.dataHoraRevogacao ? (
        <>
          {campo(
            pendente || licenca.situacao === SITUACAO.CANCELADA
              ? 'Cancelada em'
              : 'Revogada em',
            formataDataHora(licenca.dataHoraRevogacao),
            larga
          )}
          {campo('Motivo', licenca.motivoRevogacao, 'p-col-12 p-sm-8 p-lg-9 p-fluid')}
        </>
      ) : null}

      <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
        {ativa || pendente ? (
          <ButtonSp
            className="p-button-danger"
            icon="pi pi-ban"
            label={ativa ? 'Revogar instalação' : 'Cancelar chave'}
            disabled={!podeRevogar}
            title={
              ativa
                ? 'O servidor para de renovar e o sistema bloqueia ao fim da credencial atual. Para reinstalar, gere outra chave depois.'
                : 'A chave deixa de valer'
            }
            onClick={() => setDialogo('revogar')}
          />
        ) : null}
        {/* so faz sentido com a instalacao ativa e algum sinal de clone: zera a contagem
            antes de chegar ao limite (revogada nao volta — o caminho e chave nova) */}
        {ativa && licenca.clonesNaJanela > 0 ? (
          <ButtonSp
            className="p-button-warning"
            icon="pi pi-unlock"
            label="Desbloquear clone"
            disabled={!podeDesbloquear}
            title="Zera a contagem de sinais de servidor clonado (use quando for legítimo, ex.: backup restaurado)"
            onClick={() => setDialogo('desbloquear')}
          />
        ) : null}
        <ButtonSp
          className="p-button-secondary"
          label="Voltar"
          icon="pi pi-chevron-circle-left"
          keyAccess="V"
          onClick={onClose}
        />
      </div>

      {licenca.idCliente ? (
        <LicencaCrudEvento
          idCliente={Number(licenca.idCliente)}
          recarregar={recarregarEventos}
        />
      ) : null}

      {dialogo ? (
        <MessageInputText
          title={dialogo === 'revogar' ? 'Motivo' : 'Motivo do desbloqueio'}
          caption="Motivo"
          messageVisible
          handleOk={confirmaDialogo}
          handleClose={() => setDialogo('')}
        />
      ) : null}
    </>
  );
}

export default LicencaCrud;
